/** @format */
import { inject, Injectable } from '@angular/core';
import { StudyRight } from 'common-typescript/types';
import { orderBy } from 'lodash-es';
import { combineLatest, Observable, shareReplay } from 'rxjs';
import { map } from 'rxjs/operators';
import { filterStudyRightsSynchronously } from 'sis-components';
import { MyStudyRightService } from '../../common/service/my-study-right.service';
import { SelectedPlanService } from '../service/selected-plan.service';

@Injectable()
export class SelectedPlanStudyRightService {
    private readonly myStudyRightService = inject(MyStudyRightService);
    private readonly selectedPlanService = inject(SelectedPlanService);

    /**
     * Emits a study right that matches the currently selected plan and whose validity period is ongoing.
     * If the student has multiple study rights matching the selected plan, one of them is selected primarily
     * based on state (ACTIVE > ACTIVE_NONATTENDING > PASSIVE), secondarily on the latest validity start date,
     * and finally on the latest validity end date. If multiple study rights have an identical state and
     * validity period, one of them is selected at random.
     */
    readonly studyRight$: Observable<StudyRight | null> = combineLatest([
        this.myStudyRightService.studyRights$,
        this.selectedPlanService.selectedPlan$,
    ]).pipe(
        map(([studyRights, selectedPlan]) =>
            !selectedPlan
                ? []
                : filterStudyRightsSynchronously(studyRights, {
                      educationId: selectedPlan.rootId,
                      learningOpportunityId: selectedPlan.learningOpportunityId,
                      states: ['ACTIVE', 'ACTIVE_NONATTENDING', 'PASSIVE'],
                      onlyOngoing: true,
                  }),
        ),
        map(studyRights =>
            orderBy(
                studyRights,
                [
                    // The allowed state names happen to be in the desired priority order alphabetically
                    sr => sr.state,
                    // Sort by start date in reverse order, nulls last (i.e. prioritize study rights with known start dates)
                    sr => sr.valid.startDate ?? '0',
                    // Sort by end date in reverse order, nulls first (i.e. prioritize indefinite study rights)
                    sr => sr.valid.endDate ?? '9',
                ],
                ['asc', 'desc', 'desc'],
            ),
        ),
        map(([first]) => first ?? null),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
}
